import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '@nanostores/react';
import {
  projectAssetsStore,
  removeProjectAsset,
  toggleAssetReference,
  attachAllAssets,
  detachAllAssets,
  isAssetListPopupOpen,
  closeAssetListPopup,
} from '~/lib/stores/assets';
import { formatDistanceToNow } from 'date-fns';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { classNames } from '~/utils/classNames';

export const AssetListPopup: React.FC = () => {
  const assets = useStore(projectAssetsStore);
  const isOpen = useStore(isAssetListPopupOpen);

  const canAttachAll = assets.some((asset) => !asset.isReferenced);

  const handleClose = () => {
    closeAssetListPopup();
  };

  const handleDelete = (jobId: string) => {
    removeProjectAsset(jobId);
  };

  const handleToggleReference = async (jobId: string) => {
    await toggleAssetReference(jobId);
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) {
      return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'kB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-[500px] bg-dualite-background-secondary border border-dualite-border-secondary rounded-lg shadow-xl overflow-hidden flex flex-col h-[450px]"
          >
            <div className="p-4 border-b border-dualite-border-secondary flex justify-between items-start bg-dualite-background-secondary shrink-0">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-dualite-text-primary font-medium text-sm">Project Uploads</h3>
                  <button
                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors px-2 py-0.5 rounded-full bg-white/10 hover:bg-white/20 cursor-pointer"
                    onClick={() =>
                      window.open(
                        'https://alphadocs.dualite.dev/tips-and-techniques/attaching-images-videos-in-your-project',
                        '_blank',
                      )
                    }
                  >
                    <span className="text-[10px] text-dualite-text-secondary">Docs</span>
                    <div className="i-ph:link w-3 h-3" />
                  </button>
                </div>
                <p className="text-xs text-dualite-text-secondary">
                  Manage your uploads and attach/detach them in your prompt.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {assets.length > 0 &&
                  (canAttachAll ? (
                    <button
                      onClick={async () => await attachAllAssets()}
                      className="text-[11px] text-dualite-text-secondary hover:text-dualite-text-primary transition-colors"
                    >
                      Attach All
                    </button>
                  ) : (
                    <button
                      onClick={async () => await detachAllAssets()}
                      className="text-[11px] text-dualite-text-secondary hover:text-dualite-text-primary transition-colors"
                    >
                      Detach All
                    </button>
                  ))}
                <button
                  onClick={handleClose}
                  className="text-dualite-text-secondary hover:text-dualite-text-primary transition-colors ml-2"
                >
                  <div className="i-ph:x-bold text-sm" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0">
              {assets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-dualite-background-secondary flex items-center justify-center mb-2">
                    <div className="i-ph:image-square text-xl text-gray-500" />
                  </div>
                  <h4 className="text-dualite-text-primary text-sm font-medium">No uploads found</h4>
                  <p className="text-dualite-text-secondary text-xs max-w-[350px]">
                    Upload any image you want to add in your website or app.
                    <br /> <br /> You can upload jpg, jpeg, png or webp files upto 5 MBs each.
                    <br /> <br /> Upto 10 uploads per project.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-dualite-border-secondary">
                  {assets.map((asset) => (
                    <div
                      key={asset.jobId}
                      className="flex items-center gap-3 p-3 hover:bg-dualite-background-secondary transition-colors group"
                    >
                      <button
                        onClick={async () => await handleToggleReference(asset.jobId)}
                        className={classNames(
                          'w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0',
                          asset.isReferenced
                            ? 'bg-dualite-background-button/80 border-dualite-text-primary text-dualite-text-primary'
                            : 'bg-transparent border-dualite-border-secondary hover:border-dualite-text-secondary',
                        )}
                      >
                        {asset.isReferenced && (
                          <div className="i-ph:check-bold text-[10px] text-dualite-text-primary" />
                        )}
                      </button>

                      <div className="w-10 h-10 rounded bg-dualite-background-secondary flex items-center justify-center shrink-0 overflow-hidden">
                        {asset.gcsUrl && <img src={asset.gcsUrl} alt="" className="w-full h-full object-cover" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-dualite-text-primary text-sm font-medium truncate" title={asset.fileName}>
                          {asset.fileName}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-dualite-text-secondary">
                          <span>{asset.mimeType}</span>
                          <span>•</span>
                          <span>{formatSize(asset.size)}</span>
                          {asset.createdAt && (
                            <>
                              <span>•</span>
                              <span>{formatDistanceToNow(new Date(asset.createdAt), { addSuffix: true })}</span>
                            </>
                          )}
                        </div>
                      </div>

                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button className="p-1 rounded hover:bg-dualite-background-secondary text-dualite-text-secondary hover:text-dualite-text-primary transition-colors">
                            <div className="i-ph:dots-three-vertical-bold text-lg" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            className="bg-dualite-background-secondary border border-dualite-border-secondary rounded-md p-1 shadow-xl z-60 min-w-[100px]"
                            sideOffset={5}
                            align="end"
                          >
                            <DropdownMenu.Item
                              className="flex items-center gap-2 px-2 py-1.5 text-xs text-dualite-text-secondary hover:bg-red-400/50 hover:text-dualite-text-primary rounded cursor-pointer outline-none transition-colors"
                              onClick={() => handleDelete(asset.jobId)}
                            >
                              <div className="i-ph:trash-bold" />
                              Delete
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
